import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Fuel, Loader2, HardHat } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(form.email, form.password);
      toast.success("Accesso effettuato");
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      <div className="relative hidden w-1/2 lg:block">
        <img
          src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Cantiere e squadre sul campo"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#002FA7]/85 to-[#001a5e]/80" />
        <div className="absolute inset-0 flex flex-col justify-end p-12 text-white">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-white/15 backdrop-blur">
            <HardHat className="h-7 w-7" />
          </div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight">
            Powercom Creation Management
          </h1>
          <p className="mt-3 max-w-md text-base leading-relaxed text-blue-100">
            Gestione sul campo: rifornimenti carburante e rapportini di cantiere, con
            produzione, costi e margini sempre sotto controllo.
          </p>
        </div>
      </div>

      <div className="flex w-full items-center justify-center bg-slate-50 px-6 lg:w-1/2">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-2 lg:hidden">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-[#002FA7]">
              <HardHat className="h-5 w-5 text-white" />
            </div>
            <span className="font-heading text-xl font-extrabold text-slate-900">Powercom Creation</span>
          </div>

          <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">
            Accedi al tuo account
          </h2>
          <p className="mt-1 text-sm text-slate-500">Inserisci le credenziali per continuare</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                className="mt-1.5"
                placeholder="nome@azienda.it"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                className="mt-1.5"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <p className="text-sm text-rose-600" data-testid="login-error">
                {error}
              </p>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]"
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Accedi
            </Button>
          </form>

          <p className="mt-6 text-center text-xs text-slate-400">
            Gli account sono creati dall'amministratore. Contatta il responsabile per l'accesso.
          </p>
        </div>
      </div>
    </div>
  );
}
